import {
  useContext,
  useState
} from "react";

import {
  useNavigate
} from "react-router-dom";

import {
  motion
} from "framer-motion";

import {
  CartContext
} from "../context/CartContext";

import {
  WishlistContext
} from "../context/WishlistContext";

import {
  useAuth
} from "../context/AuthContext";

import {
  useAuthModal
} from "../context/AuthModalContext";

import {
  FaHeart,
  FaStar
} from "react-icons/fa";

import LuxuryImage
  from "./LuxuryImage";

function ProductCard({ product }) {

  const navigate = useNavigate();

  const { user } =
    useAuth();

  const {
    openAuthModal
  } = useAuthModal();

  const { addToCart } =
    useContext(CartContext);

  const {
    addToWishlist,
    removeFromWishlist,
    isWishlisted,
    wishlist
  } = useContext(WishlistContext);

  const [hovered, setHovered] =
    useState(false);

  /* ===============================
     SAFETY
  =============================== */

  if (!product) return null;

  /* ===============================
     IMAGES
  =============================== */

  const mainImage =
    product?.images?.[0]?.imageUrl ||
    "/images/placeholder.png";

  const hoverImage =
    product?.images?.[1]?.imageUrl ||
    mainImage;

  /* ===============================
     WISHLIST
  =============================== */

  const inWishlist =
    product?.id
      ? isWishlisted(product.id)
      : false;

  const handleWishlistToggle = (e) => {

    e.stopPropagation();

    /* AUTH CHECK */

   if (!user) {

     const variantId =
       product?.variants?.[0]?.id;

     localStorage.setItem(
       "pendingCartVariant",
       variantId
     );

     openAuthModal("/cart");

     return;

   }

    if (!product?.id) return;

    if (inWishlist) {

      const item = wishlist.find(
        (w) =>
          w.product?.id === product.id
      );

      if (item) {

        removeFromWishlist(item.id);

      }

    } else {

      addToWishlist(product.id);

    }

  };

  /* ===============================
     CART
  =============================== */

  const handleAddToCart = (e) => {

    e.stopPropagation();

    /* AUTH CHECK */

    if (!user) {

      const variantId =
        product?.variants?.[0]?.id;

      if (variantId) {

        localStorage.setItem(
          "pendingCartVariant",
          variantId
        );

      }

      openAuthModal("/cart");

      return;

    }

    const variantId =
      product?.variants?.[0]?.id;

    if (!variantId) {

      alert("Variant unavailable");

      return;

    }

    addToCart(variantId);

    navigate("/checkout");

  };

  /* ===============================
     PRICE
  =============================== */

  const price =
    product?.price || 0;

  const original =
    product?.originalPrice || price;

  const discountPercent =
    original > price
      ? Math.round(
          ((original - price) /
            original) * 100
        )
      : 0;

  /* ===============================
     UI
  =============================== */

  return (

    <motion.div

      whileHover={{
        y: -10
      }}

      transition={{
        duration: 0.45
      }}

      className="
        group

        relative

        rounded-[2.2rem]

        overflow-hidden

        bg-white/80

        backdrop-blur-xl

        border border-white/60

        shadow-[0_10px_40px_rgba(0,0,0,0.06)]

        hover:shadow-[0_25px_60px_rgba(0,0,0,0.14)]

        transition-all duration-700

        cursor-pointer
      "

      onClick={() =>
        navigate(`/product/${product.id}`)
      }

      onMouseEnter={() =>
        setHovered(true)
      }

      onMouseLeave={() =>
        setHovered(false)
      }
    >

      {/* ===============================
          IMAGE AREA
      =============================== */}

      <div className="
        relative

        overflow-hidden

        bg-[#F7F3EE]
      ">

        {/* BADGE */}

        <div className="
          absolute top-5 left-5
          z-30
        ">

          <span className="
            px-4 py-2

            rounded-full

            bg-white/85
            backdrop-blur-xl

            text-[10px]

            uppercase

            tracking-[0.25em]

            text-stone-700

            shadow-lg
          ">

            Handmade

          </span>

        </div>

        {/* WISHLIST */}

        <motion.button

          whileTap={{
            scale: 0.9
          }}

          whileHover={{
            scale: 1.08
          }}

          onClick={handleWishlistToggle}

          className={`
            absolute top-5 right-5

            z-30

            w-12 h-12

            rounded-full

            backdrop-blur-xl

            shadow-xl

            flex items-center justify-center

            transition-all duration-500

            ${
              inWishlist
                ? `
                  bg-red-500
                  text-white
                `
                : `
                  bg-white/85
                  text-stone-700
                `
            }
          `}
        >

          <FaHeart size={14} />

        </motion.button>

        {/* IMAGE */}

        <div className="
          relative
          overflow-hidden
        ">

          <LuxuryImage
            src={
              hovered
                ? hoverImage
                : mainImage
            }
            alt={product?.name}
            className="
              w-full

              h-[360px]

              object-cover

              transition duration-1000

              group-hover:scale-110
            "
          />

          {/* OVERLAY */}

          <div className="
            absolute inset-0

            bg-gradient-to-t
            from-black/30
            via-transparent
            to-transparent

            opacity-0

            group-hover:opacity-100

            transition duration-700
          " />

        </div>

      </div>

      {/* ===============================
          CONTENT
      =============================== */}

      <div className="
        p-6
      ">

        {/* COLLECTION */}

        <p className="
          text-[10px]

          uppercase

          tracking-[0.35em]

          text-stone-400

          mb-3
        ">

          {product?.collectionName ||
            "Sacred Collection"}

        </p>

        {/* TITLE */}

        <h3 className="
          text-2xl

          font-serif
          italic

          text-stone-900

          mb-3

          line-clamp-1
        ">

          {product?.name}

        </h3>

        {/* RATING */}

        <div className="
          flex items-center
          gap-1

          mb-4
        ">

          {[1,2,3,4,5].map((star) => (

            <FaStar
              key={star}
              size={12}
              className={
                star <=
                Math.round(
                  product?.avgRating || 0
                )
                  ? "text-[#D4A74F]"
                  : "text-stone-300"
              }
            />

          ))}

          <span className="
            text-xs
            text-stone-400
            ml-2
          ">

            ({product?.avgRating || "4.9"})

          </span>

        </div>

        {/* PRICE */}

        <div className="
          flex items-center
          gap-3
          flex-wrap

          mb-6
        ">

          <span className="
            text-2xl
            font-semibold
            text-stone-900
          ">

            ₹{price}

          </span>

          {original > price && (

            <span className="
              text-stone-400
              line-through
            ">

              ₹{original}

            </span>

          )}

          {discountPercent > 0 && (

            <span className="
              text-xs

              px-3 py-1

              rounded-full

              bg-green-100
              text-green-700

              uppercase

              tracking-wide
            ">

              {discountPercent}% OFF

            </span>

          )}

        </div>

        {/* BUTTON */}

        <motion.button

          whileHover={{
            scale: 1.02
          }}

          whileTap={{
            scale: 0.98
          }}

          onClick={handleAddToCart}

          className="
            w-full

            py-4

            rounded-full

            bg-black

            text-white

            text-sm

            uppercase

            tracking-[0.25em]

            hover:bg-[#1C1C1C]

            transition-all duration-500
          "
        >

          Add to Cart

        </motion.button>

      </div>

    </motion.div>

  );

}

export default ProductCard;